import React, { useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useGlossaryBlueprintsDataContextOptional } from '@/providers/GlossaryBlueprintsDataProvider';
import { useGlossaryDataContextOptional } from "@/providers/ObjectsDataProvider";
import { useDialogDataContextOptional } from "@/providers/DialogDataProvider";
import { useDocumentDataContext } from "@/providers/DocumentDataProvider";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { Button } from '@/components/ui/button';
import { Copy, Check, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type ItemType = 'object' | 'template' | 'document' | 'dialog';

export default function StructureSidebarDataInfo() {
  const { t } = usePlatformTranslation();
  const params = useParams();
  const location = useLocation();
  const { toast } = useToast();
  const { selectedGame } = useSimplifiedUserData();

  // Get templates, objects, and dialogs context (using optional hooks to avoid errors when outside providers)
  const glossaryBlueprintsContext = useGlossaryBlueprintsDataContextOptional();
  const glossaryDataContext = useGlossaryDataContextOptional();
  const dialogDataContext = useDialogDataContextOptional();
  const { documents } = useDocumentDataContext();

  // State for copy feedback
  const [copied, setCopied] = useState(false);

  // Determine current context and item
  const currentContext = useMemo(() => {
    const path = location.pathname;
    
    if (path.startsWith('/library/') && params.itemId) {
      return {
        itemType: 'object' as ItemType,
        itemId: params.itemId,
      };
    }
    
    if (path.startsWith('/templates/') && params.itemId) {
      return {
        itemType: 'template' as ItemType,
        itemId: params.itemId,
      };
    }
    
    if (path.startsWith('/documents/') && params.documentId) {
      return {
        itemType: 'document' as ItemType,
        itemId: params.documentId,
      };
    }
    
    if (path.startsWith('/dialogs/') && params.dialogId) {
      return {
        itemType: 'dialog' as ItemType,
        itemId: params.dialogId,
      };
    }
    
    return null;
  }, [location.pathname, params]);

  // Get current item from store (access context values directly inside useMemo to avoid dependency issues)
  const currentItem = useMemo(() => {
    if (!currentContext) return null;

    const glossaryItems = glossaryDataContext?.glossaryItems ?? [];
    const glossaryBlueprints = glossaryBlueprintsContext?.glossaryBlueprints ?? [];
    const dialogs = dialogDataContext?.dialogs ?? [];

    if (currentContext.itemType === 'object') {
      return glossaryItems.find(item => item.id === currentContext.itemId);
    }
    
    if (currentContext.itemType === 'template') {
      return glossaryBlueprints.find(bp => bp.id === currentContext.itemId);
    }

    if (currentContext.itemType === 'dialog') {
      return dialogs.find(dialog => dialog.id === currentContext.itemId);
    }

    if (currentContext.itemType === 'document') {
      return documents.find(doc => doc.id === currentContext.itemId);
    }

    return null;
  }, [currentContext, glossaryDataContext?.glossaryItems, glossaryBlueprintsContext?.glossaryBlueprints, dialogDataContext?.dialogs, documents]);

  // Format JSON data
  const formattedJson = useMemo(() => {
    if (!currentItem) return '';
    return JSON.stringify(currentItem, null, 2);
  }, [currentItem]);

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedJson);
      setCopied(true);
      toast({
        title: 'Success',
        description: 'JSON data copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: t('platform.common.error'),
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  // Handle download as JSON file
  const handleDownload = () => {
    if (!currentItem) return;

    const itemName = (currentItem as any).name || 'item';
    const fileName = `${itemName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${currentContext?.itemType}_${Date.now()}.json`;
    
    const blob = new Blob([formattedJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Success',
      description: `Downloaded as ${fileName}`,
    });
  };

  // Show empty state if no item selected
  if (!currentContext || !currentItem) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>Select an item to view data</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Data Information
          </h3>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopy}
              className="h-8"
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownload}
              className="h-8"
            >
              <Download className="h-3 w-3 mr-1" />
              Download
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {currentContext.itemType.charAt(0).toUpperCase() + currentContext.itemType.slice(1)} data in JSON format
        </p>
      </div>

      {/* JSON Display */}
      <div className="flex-1 overflow-y-auto p-4">
        <pre className="text-xs font-mono bg-muted p-4 rounded-md border border-border overflow-x-auto">
          <code className="text-foreground whitespace-pre">{formattedJson}</code>
        </pre>
      </div>

      {/* Footer Info */}
      <div className="p-3 border-t border-border bg-muted">
        <div className="text-xs text-muted-foreground space-y-1">
          <div className="flex justify-between">
            <span>Type:</span>
            <span className="font-medium text-foreground capitalize">{currentContext.itemType}</span>
          </div>
          <div className="flex justify-between">
            <span>ID:</span>
            <span className="font-mono text-foreground">{currentItem.id}</span>
          </div>
          {(currentItem as any).version && (
            <div className="flex justify-between">
              <span>Version:</span>
              <span className="font-medium text-foreground">
                {typeof (currentItem as any).version === 'object' 
                  ? `${(currentItem as any).version.major ?? 0}.${(currentItem as any).version.minor ?? 0}.${(currentItem as any).version.patch ?? 0}`
                  : (currentItem as any).version}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
